import {
  action, computed, makeAutoObservable, observable, observe, runInAction, when,
} from 'mobx';
import {
  AppGame, getGameUrl, isGameGemsCompleted, UserGame,
} from '@gamiphy/bl/game';
import { GameApi } from '@gamiphy/gamix-modules/game';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { Site } from '@gamiphy/bl/site';
import { SiteApi } from '@gamiphy/gamix-modules/site';
import { Http } from '@gamiphy/http';

class GameStore {
  @observable initialized = false;

  @observable started = false;

  @observable appGame?: AppGame = undefined;

  @observable site?: Site = undefined;

  @observable userGame?: UserGame = undefined;

  constructor() {
    makeAutoObservable(this);

    when(() => AppStore.initialized, () => {
      void (async () => {
        const [appGame] = await GameApi.getAppGames({
          enabled: true,
        });

        runInAction(() => {
          this.appGame = appGame;
        });

        if (this.appGame && !this.appGame.url) {
          const appSites = await SiteApi.listSites({
            'data.gameId': this.appGame.id,
          });

          runInAction(() => {
            this.site = appSites.find((site) => site.data?.gameId === this.appGame?.id);
          });
        }

        runInAction(() => {
          this.initialized = true;
        });
      })();
    });

    when(() => UserStore.isLoggedIn && !!this.appGame, () => {
      if (this.appGame) {
        void GameApi.getUserGameFromAppGame(this.appGame.id)
          .then((userGame) => {
            runInAction(() => {
              this.userGame = userGame;
              this.initialized = true;
            });
          });
      }
    });

    observe(UserStore, 'user', () => {
      if (UserStore.user) {
        void this.reloadGameUser();
      }
    });
  }

  @computed get completed(): boolean {
    return (this.appGame && this.userGame) ? isGameGemsCompleted(this.appGame, this.userGame) : false;
  }

  @computed get hasGames(): boolean {
    return this.initialized && !!this.appGame;
  }

  @computed get gameUrl(): string | undefined {
    const url = this.site ? Http.buildUri(this.site.domain) : this.appGame?.url;
    return url ? getGameUrl(url, this.userGame?.user) : undefined;
  }

  @computed get isGameEnabled(): boolean {
    return !!this.appGame?.enabled;
  }

  @computed
  public get enabled(): boolean {
    return this.appGame?.enabled ?? false;
  }

  @action
  async reloadGameUser(): Promise<void> {
    if (this.appGame && this.userGame) {
      const userGame = await GameApi.getUserGameFromAppGame(this.appGame.id);

      runInAction(() => {
        this.userGame = userGame;
      });
    }
  }
}

export default new GameStore();
